import React from "react";

import { Dev } from "../util/consoleUtil";
import genCode from "../util/genCode";

import MainModalSimple from "../../components/MainModalSimple";
import ModalActivity from "../../components/ModalActivity";
import ModalImage from "../../components/ModalImage";

import { ModalActivityType, ModalType, OnlyChildren } from "../../types/common";

type ModalContextType = {
  pushAlert: (alert: ModalType) => void;
  alertConfirm: (message: string, onConfirm: Promise<void> | (() => void)) => void;
  showImage: (imageUrl: string) => void;
  setActivity: (alert: ModalActivityType) => void;
  resetActivity: () => void;
};

const ModalContext = React.createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: OnlyChildren) {
  const [alerts, setAlerts] = React.useState<ModalType[]>([]);
  const [activityAlert, setActivityAlert] = React.useState<ModalActivityType | null>(null);
  const [imageAlert, setImageAlert] = React.useState<string | null>(null);

  const SHOW_LOG = false;

  const pushAlert = (alert: ModalType) => {
    const id = genCode();
    SHOW_LOG && console.log(...new Dev("Creating:", id).Info());

    setAlerts((oldState) => [...oldState, { ...alert, id }]);
  };

  const showImage = (imageUrl: string) => {
    setImageAlert(imageUrl);
  };

  const setActivity = (alert: ModalActivityType) => {
    setActivityAlert({ ...alert });
  };

  const resetActivity = () => {
    setActivityAlert(null);
  };

  const alertConfirm = (message: string, onConfirm: Promise<void> | (() => void)) => {
    const newState = [
      ...alerts,
      { message, isDelete: true, onConfirm, id: genCode() } as ModalType,
    ];
    setAlerts(newState);
  };

  const handleCloseOneModal = (alertId: string | number) => {
    SHOW_LOG && console.log(...new Dev("Closing:", alertId).Error());

    setAlerts(
      alerts.filter((alertIterator) => {
        return String(alertIterator.id) !== String(alertId);
      })
    );
  };

  React.useEffect(() => {
    SHOW_LOG && console.log(...new Dev("Mounted ModalProvider").Log());
  }, []);

  React.useEffect(() => {
    SHOW_LOG && console.log(...new Dev("Alerts remain:", alerts.length).Info());
  }, [alerts]);

  SHOW_LOG && console.log(...new Dev("Building ModalProvider").Log());

  const value = { pushAlert, alertConfirm, showImage, setActivity, resetActivity };

  return (
    <ModalContext.Provider value={value}>
      {alerts.map((alert) => (
        <MainModalSimple
          key={alert.id}
          title={alert.title}
          children={alert.children}
          message={alert.message}
          type={alert.type}
          isClosable={alert.isClosable}
          isConfirm={alert.isConfirm}
          onClose={() => handleCloseOneModal(alert?.id ?? "")}
          onConfirm={alert.onConfirm}
          onDimiss={alert.onDimiss}
        />
      ))}

      {imageAlert ? <ModalImage imageUrl={imageAlert} onClose={() => setImageAlert(null)} /> : null}

      {activityAlert ? (
        <ModalActivity
          message={activityAlert.message}
          type={activityAlert.type}
          onClose={() => setActivityAlert(null)}
        />
      ) : null}

      {children}
    </ModalContext.Provider>
  );
}

export default function useModal() {
  return React.useContext(ModalContext) as ModalContextType;
}
